import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { BudgetsPage } from './pages/BudgetsPage';
import { BudgetDetailPage } from './pages/BudgetDetailPage';
import { BudgetEditorPage } from './pages/BudgetEditorPage';
import { BudgetDocumentPage } from './pages/BudgetDocumentPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettingsPage } from './pages/SettingsPage';
import { SuperAdminPage } from './pages/SuperAdminPage';
import { SuperAdminUsersPage } from './pages/SuperAdminUsersPage';
import { PortfolioManagePage } from './pages/PortfolioManagePage';
import { PublicPortfolioPage } from './pages/PublicPortfolioPage';
import { MainLayout } from './layouts/MainLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { useAuth } from './context/AuthContext';
import { LoadingScreen } from './components/ui/LoadingScreen';

function PrivateRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen message="Verificando..." />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PublicRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen message="Cargando..." />;
  if (isAuthenticated) return <Navigate to={user?.role === 'superadmin' ? '/superadmin' : '/dashboard'} replace />;
  return <Outlet />;
}

function HomeRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen message="Cargando..." />;
  return <Navigate to={user?.role === 'superadmin' ? '/superadmin' : '/dashboard'} replace />;
}

function SuperAdminRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingScreen message="Verificando..." />;
  if (!user || user.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

export default function App() {
  const { isLoading } = useAuth();
  if (isLoading) return <LoadingScreen message="Inicializando ReformAll..." />;
  return (
    <Routes>
      <Route path="/portfolio/:slug" element={<PublicPortfolioPage />} />
      <Route path="/" element={<HomeRedirect />} />

      <Route element={<AuthLayout />}>
        <Route element={<PublicRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Route>
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route element={<PrivateRoute />}>
        <Route path="/budgets/:id/view" element={<BudgetDocumentPage />} />
      </Route>

      <Route element={<MainLayout />}>
        <Route element={<PrivateRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/new" element={<Navigate to="/clients" replace />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />

          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/new" element={<Navigate to="/projects" replace />} />
          <Route path="/projects/:id" element={<ProjectDetailPage />} />

          <Route path="/budgets" element={<BudgetsPage />} />
          <Route path="/budgets/new" element={<BudgetEditorPage />} />
          <Route path="/budgets/:id" element={<BudgetDetailPage />} />
          <Route path="/budgets/:id/edit" element={<BudgetEditorPage />} />

          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/expenses/new" element={<Navigate to="/expenses" replace />} />

          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/portfolio" element={<PortfolioManagePage />} />

          <Route element={<SuperAdminRoute />}>
            <Route path="/superadmin" element={<SuperAdminPage />} />
            <Route path="/superadmin/users" element={<SuperAdminUsersPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}
